import { useEffect, useState } from 'react';
import { liveChatAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  MessageSquare, Settings, Users, BarChart3, Clock, Globe, Palette,
  Bot, Zap, Bell, Save, Loader2, Plus, X, Code
} from 'lucide-react';

export const LiveChatSettingsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    is_enabled: true,
    chat_title: 'Chat with us',
    welcome_message: 'Hi! How can we help you today?',
    offline_message: "We're currently offline. Please leave a message.",
    bot_enabled: true,
    bot_name: 'Assistant',
    bot_avatar_url: '',
    auto_responses: [],
    escalation_keywords: ['agent', 'human', 'help', 'speak to someone'],
    primary_color: '#3b82f6',
    position: 'bottom-right',
    business_hours_enabled: false,
    business_hours: {
      monday: { start: '09:00', end: '17:00', enabled: true },
      tuesday: { start: '09:00', end: '17:00', enabled: true },
      wednesday: { start: '09:00', end: '17:00', enabled: true },
      thursday: { start: '09:00', end: '17:00', enabled: true },
      friday: { start: '09:00', end: '17:00', enabled: true },
      saturday: { start: '10:00', end: '14:00', enabled: false },
      sunday: { start: '10:00', end: '14:00', enabled: false }
    },
    timezone: 'America/Toronto',
    collect_email: true,
    collect_name: true,
    collect_phone: false,
    require_email: false,
    notification_sound: true,
    email_transcripts: true,
    transcript_email: ''
  });

  const [newKeyword, setNewKeyword] = useState('');
  const [newAutoResponse, setNewAutoResponse] = useState({ trigger: '', response: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await liveChatAPI.getSettings();
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await liveChatAPI.updateSettings(settings);
      toast.success('Live chat settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !settings.escalation_keywords.includes(newKeyword.trim())) {
      setSettings(prev => ({
        ...prev,
        escalation_keywords: [...prev.escalation_keywords, newKeyword.trim()]
      }));
      setNewKeyword('');
    }
  };

  const removeKeyword = (keyword) => {
    setSettings(prev => ({
      ...prev,
      escalation_keywords: prev.escalation_keywords.filter(k => k !== keyword)
    }));
  };

  const addAutoResponse = () => {
    if (newAutoResponse.trigger.trim() && newAutoResponse.response.trim()) {
      setSettings(prev => ({
        ...prev,
        auto_responses: [...(prev.auto_responses || []), { ...newAutoResponse }]
      }));
      setNewAutoResponse({ trigger: '', response: '' });
    }
  };

  const removeAutoResponse = (index) => {
    setSettings(prev => ({
      ...prev,
      auto_responses: prev.auto_responses.filter((_, i) => i !== index)
    }));
  };

  if (!hasRole(['admin'])) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="p-8 text-center">
          <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">Only administrators can manage live chat settings.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="live-chat-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6" />
            Live Chat Settings
          </h1>
          <p className="text-muted-foreground">
            Configure your live chat widget and agent settings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild data-testid="embed-code-btn">
            <a href="/live-chat/embed">
              <Code className="h-4 w-4 mr-2" />
              Get Embed Code
            </a>
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general"><Settings className="h-4 w-4 mr-2" />General</TabsTrigger>
          <TabsTrigger value="bot"><Bot className="h-4 w-4 mr-2" />AI Bot</TabsTrigger>
          <TabsTrigger value="appearance"><Palette className="h-4 w-4 mr-2" />Appearance</TabsTrigger>
          <TabsTrigger value="hours"><Clock className="h-4 w-4 mr-2" />Business Hours</TabsTrigger>
          <TabsTrigger value="data"><Users className="h-4 w-4 mr-2" />Data Collection</TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Chat Widget</CardTitle>
              <CardDescription>Basic chat widget settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Live Chat</p>
                  <p className="text-sm text-muted-foreground">Show the chat widget on your website</p>
                </div>
                <Switch
                  checked={settings.is_enabled}
                  onCheckedChange={(v) => setSettings(p => ({ ...p, is_enabled: v }))}
                  data-testid="enable-chat-toggle"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Chat Title</Label>
                  <Input
                    value={settings.chat_title}
                    onChange={(e) => setSettings(p => ({ ...p, chat_title: e.target.value }))}
                    placeholder="Chat with us"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select value={settings.timezone} onValueChange={(v) => setSettings(p => ({ ...p, timezone: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Toronto">Eastern (Toronto)</SelectItem>
                      <SelectItem value="America/Chicago">Central (Chicago)</SelectItem>
                      <SelectItem value="America/Denver">Mountain (Denver)</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific (LA)</SelectItem>
                      <SelectItem value="Europe/London">GMT (London)</SelectItem>
                      <SelectItem value="Europe/Paris">CET (Paris)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Welcome Message</Label>
                <Textarea
                  value={settings.welcome_message}
                  onChange={(e) => setSettings(p => ({ ...p, welcome_message: e.target.value }))}
                  placeholder="Hi! How can we help you today?"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Offline Message</Label>
                <Textarea
                  value={settings.offline_message}
                  onChange={(e) => setSettings(p => ({ ...p, offline_message: e.target.value }))}
                  placeholder="We're currently offline..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notifications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Notification Sound</p>
                  <p className="text-sm text-muted-foreground">Play sound for new messages</p>
                </div>
                <Switch
                  checked={settings.notification_sound}
                  onCheckedChange={(v) => setSettings(p => ({ ...p, notification_sound: v }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Transcripts</p>
                  <p className="text-sm text-muted-foreground">Send chat transcripts via email</p>
                </div>
                <Switch
                  checked={settings.email_transcripts}
                  onCheckedChange={(v) => setSettings(p => ({ ...p, email_transcripts: v }))}
                />
              </div>
              {settings.email_transcripts && (
                <div className="space-y-2">
                  <Label>Transcript Email</Label>
                  <Input
                    type="email"
                    value={settings.transcript_email}
                    onChange={(e) => setSettings(p => ({ ...p, transcript_email: e.target.value }))}
                    placeholder="support@company.com"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Bot Tab */}
        <TabsContent value="bot" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>AI Bot Settings</CardTitle>
              <CardDescription>Configure the automated bot that handles initial conversations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="font-medium">Enable AI Bot</p>
                  <p className="text-sm text-muted-foreground">Bot handles chats before escalating to agents</p>
                </div>
                <Switch
                  checked={settings.bot_enabled}
                  onCheckedChange={(v) => setSettings(p => ({ ...p, bot_enabled: v }))}
                  data-testid="enable-bot-toggle"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bot Name</Label>
                  <Input
                    value={settings.bot_name}
                    onChange={(e) => setSettings(p => ({ ...p, bot_name: e.target.value }))}
                    placeholder="Assistant"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bot Avatar URL (optional)</Label>
                  <Input
                    value={settings.bot_avatar_url || ''}
                    onChange={(e) => setSettings(p => ({ ...p, bot_avatar_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Escalation Keywords</Label>
                <p className="text-xs text-muted-foreground">When visitors use these words, they'll be connected to an agent</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {settings.escalation_keywords?.map((keyword) => (
                    <Badge key={keyword} variant="secondary" className="flex items-center gap-1">
                      {keyword}
                      <X className="h-3 w-3 cursor-pointer hover:text-red-500" onClick={() => removeKeyword(keyword)} />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    placeholder="Add keyword..."
                    onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                  />
                  <Button type="button" variant="outline" onClick={addKeyword}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Auto Responses</CardTitle>
              <CardDescription>Set up automatic responses for common questions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings.auto_responses?.map((ar, index) => (
                <div key={index} className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium">Trigger: "{ar.trigger}"</p>
                    <p className="text-sm text-muted-foreground">{ar.response}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeAutoResponse(index)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="space-y-2 p-4 border rounded-lg">
                <Input
                  value={newAutoResponse.trigger}
                  onChange={(e) => setNewAutoResponse(p => ({ ...p, trigger: e.target.value }))}
                  placeholder="Trigger phrase (e.g., 'hours', 'pricing')"
                />
                <Textarea
                  value={newAutoResponse.response}
                  onChange={(e) => setNewAutoResponse(p => ({ ...p, response: e.target.value }))}
                  placeholder="Bot response..."
                  rows={2}
                />
                <Button type="button" variant="outline" onClick={addAutoResponse}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Auto Response
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Widget Appearance</CardTitle>
              <CardDescription>Customize how the chat widget looks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => setSettings(p => ({ ...p, primary_color: e.target.value }))}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => setSettings(p => ({ ...p, primary_color: e.target.value }))}
                      placeholder="#3b82f6"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Widget Position</Label>
                  <Select value={settings.position} onValueChange={(v) => setSettings(p => ({ ...p, position: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bottom-right">Bottom Right</SelectItem>
                      <SelectItem value="bottom-left">Bottom Left</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-6 p-6 bg-slate-100 rounded-lg">
                <p className="text-sm text-muted-foreground mb-4">Preview</p>
                <div className="relative h-[200px] bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="absolute bottom-4 right-4">
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                      style={{ backgroundColor: settings.primary_color }}
                    >
                      <MessageSquare className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Hours Tab */}
        <TabsContent value="hours" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Business Hours</CardTitle>
              <CardDescription>Set when live agents are available</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Business Hours</p>
                  <p className="text-sm text-muted-foreground">Show offline message outside business hours</p>
                </div>
                <Switch
                  checked={settings.business_hours_enabled}
                  onCheckedChange={(v) => setSettings(p => ({ ...p, business_hours_enabled: v }))}
                />
              </div>

              {settings.business_hours_enabled && (
                <div className="space-y-3">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                    <div key={day} className="flex items-center gap-4 p-3 border rounded-lg">
                      <div className="w-28 capitalize font-medium">{day}</div>
                      <Switch
                        checked={settings.business_hours?.[day]?.enabled ?? false}
                        onCheckedChange={(v) => setSettings(p => ({
                          ...p,
                          business_hours: {
                            ...p.business_hours,
                            [day]: { ...p.business_hours?.[day], enabled: v }
                          }
                        }))}
                      />
                      {settings.business_hours?.[day]?.enabled && (
                        <>
                          <Input
                            type="time"
                            value={settings.business_hours?.[day]?.start || '09:00'}
                            onChange={(e) => setSettings(p => ({
                              ...p,
                              business_hours: {
                                ...p.business_hours,
                                [day]: { ...p.business_hours?.[day], start: e.target.value }
                              }
                            }))}
                            className="w-32"
                          />
                          <span>to</span>
                          <Input
                            type="time"
                            value={settings.business_hours?.[day]?.end || '17:00'}
                            onChange={(e) => setSettings(p => ({
                              ...p,
                              business_hours: {
                                ...p.business_hours,
                                [day]: { ...p.business_hours?.[day], end: e.target.value }
                              }
                            }))}
                            className="w-32"
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Collection Tab */}
        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Visitor Data Collection</CardTitle>
              <CardDescription>Configure what information to collect from visitors</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Collect Name</p>
                  <p className="text-sm text-muted-foreground">Ask visitors for their name</p>
                </div>
                <Switch
                  checked={settings.collect_name}
                  onCheckedChange={(v) => setSettings(p => ({ ...p, collect_name: v }))}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Collect Email</p>
                  <p className="text-sm text-muted-foreground">Ask visitors for their email</p>
                </div>
                <Switch
                  checked={settings.collect_email}
                  onCheckedChange={(v) => setSettings(p => ({ ...p, collect_email: v }))}
                />
              </div>
              {settings.collect_email && (
                <div className="flex items-center justify-between p-3 border rounded-lg ml-6">
                  <div>
                    <p className="font-medium">Require Email</p>
                    <p className="text-sm text-muted-foreground">Email is mandatory to start chat</p>
                  </div>
                  <Switch
                    checked={settings.require_email}
                    onCheckedChange={(v) => setSettings(p => ({ ...p, require_email: v }))}
                  />
                </div>
              )}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Collect Phone</p>
                  <p className="text-sm text-muted-foreground">Ask visitors for their phone number</p>
                </div>
                <Switch
                  checked={settings.collect_phone}
                  onCheckedChange={(v) => setSettings(p => ({ ...p, collect_phone: v }))}
                />
              </div>

              <div className="p-4 bg-blue-50 rounded-lg mt-4">
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Automatic Data Collection</p>
                    <p className="text-sm text-blue-700">
                      We automatically collect visitor IP location, device type, browser, referrer source, 
                      and pages visited to help you understand your customers better.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LiveChatSettingsPage;
